import type { VaultAppToastEvent } from '@nebula/shared-vault/data-access';

const VAULT_SUCCESS_TOAST_DURATION = 3500;
const VAULT_ACTION_TOAST_DURATION = 3200;

function vaultSuccessToast(title: string, message: string): VaultAppToastEvent {
  return {
    type: 'success',
    title,
    message,
    duration: VAULT_SUCCESS_TOAST_DURATION,
  };
}

/** Toast derecha: feedback de acción puntual (éxito/error/info/warning). */
export function buildVaultActionToast(
  type: VaultAppToastEvent['type'],
  title: string,
  message: string,
  duration = VAULT_ACTION_TOAST_DURATION,
): VaultAppToastEvent {
  return { type, title, message, duration };
}

export function buildVaultErrorToast(title: string, message: string): VaultAppToastEvent {
  return buildVaultActionToast('error', title, message);
}

export function buildRetentionPolicyCreatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Política creada',
    `La política «${name}» se registró correctamente.`,
  );
}

export function buildRetentionPolicyUpdatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Política actualizada',
    `Los cambios en «${name}» se guardaron correctamente.`,
  );
}

export function buildLegalHoldCaseCreatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast('Caso creado', `El caso «${name}» se registró correctamente.`);
}

export function buildLegalHoldCaseUpdatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Caso actualizado',
    `Los cambios en «${name}» se guardaron correctamente.`,
  );
}

export function buildVaultSuccessToast(title: string, message: string): VaultAppToastEvent {
  return vaultSuccessToast(title, message);
}

export function buildTenantCreatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast('Empresa creada', `La empresa «${name}» se registró correctamente.`);
}

export function buildTenantUpdatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Empresa actualizada',
    `Los cambios en «${name}» se guardaron correctamente.`,
  );
}

export function buildTenantDeletedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast('Empresa eliminada', `La empresa «${name}» se eliminó correctamente.`);
}

export function buildTenantActivatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Empresa activada',
    `La empresa «${name}» quedó activa. Sus usuarios pueden operar con normalidad.`,
  );
}

export function buildTenantDeactivatedToast(name: string): VaultAppToastEvent {
  return vaultSuccessToast(
    'Empresa desactivada',
    `La empresa «${name}» quedó inactiva. No aparecerá al asignar usuarios hasta reactivarla.`,
  );
}

export interface TenantConfirmDialogConfig {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

export function buildTenantDeleteConfirmDialog(
  tenant: Pick<{ name?: string }, 'name'>,
): TenantConfirmDialogConfig {
  const name = tenant.name?.trim() || 'esta empresa';
  return {
    title: 'Eliminar empresa',
    message: `¿Eliminar «${name}»? Esta acción puede afectar usuarios y permisos asociados.`,
    confirmLabel: 'Eliminar',
    variant: 'error',
  };
}

export function buildTenantActivateConfirmDialog(
  tenant: Pick<{ name?: string }, 'name'>,
): TenantConfirmDialogConfig {
  const name = tenant.name?.trim() || 'esta empresa';
  return {
    title: 'Activar empresa',
    message: `¿Activar «${name}»? Los usuarios asignados podrán volver a operar en Vault.`,
    confirmLabel: 'Activar',
    variant: 'info',
  };
}

export function buildTenantDeactivateConfirmDialog(
  tenant: Pick<{ name?: string }, 'name'>,
): TenantConfirmDialogConfig {
  const name = tenant.name?.trim() || 'esta empresa';
  return {
    title: 'Desactivar empresa',
    message: `¿Desactivar «${name}»? No aparecerá al crear o reasignar usuarios hasta que la reactive.`,
    confirmLabel: 'Desactivar',
    variant: 'warning',
  };
}
